import { useState } from 'react';
import { Modal } from './Modal';

export interface ColumnSpec {
  key: string;
  label: string;
  required?: boolean;
  hint?: string;
}

interface Props<TRow> {
  open: boolean;
  title: string;
  columns: ColumnSpec[];
  /** Examples to show users for the CSV format. */
  exampleRows: string[];
  /** Convert a parsed row (key→value) into the input that createRow expects. */
  toInput: (row: Record<string, string>) => TRow | null;
  /** Per-row create function. Receives the typed input. */
  createRow: (row: TRow) => Promise<void>;
  onClose: () => void;
  /** Called after successful import so the parent can refresh. */
  onImported: (count: number) => void;
}

/**
 * Generic CSV bulk-import modal. Paste lines, parse, preview, import.
 * Robust to extra whitespace and trailing commas. Skips blank lines.
 */
export function BulkImportModal<TRow>({
  open, title, columns, exampleRows, toInput, createRow, onClose, onImported,
}: Props<TRow>) {
  const [csv, setCsv] = useState('');
  const [parsed, setParsed] = useState<Array<{ raw: string; row: Record<string, string>; ok: boolean; reason?: string }>>([]);
  const [step, setStep] = useState<'paste' | 'preview' | 'importing' | 'done'>('paste');
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  function parseCsv(): void {
    setError(null);
    const lines = csv.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) { setError('Paste at least one line.'); return; }
    // Auto-detect header row: if first line contains any column label, assume header.
    let dataLines = lines;
    const firstLower = lines[0].toLowerCase();
    if (columns.some((c) => firstLower.includes(c.label.toLowerCase()) || firstLower.includes(c.key.toLowerCase()))) {
      dataLines = lines.slice(1);
    }
    const out: Array<{ raw: string; row: Record<string, string>; ok: boolean; reason?: string }> = [];
    for (const line of dataLines) {
      const cells = splitCsvLine(line);
      const row: Record<string, string> = {};
      columns.forEach((c, i) => { row[c.key] = (cells[i] ?? '').trim(); });
      // Validate required
      const missingRequired = columns.find((c) => c.required && !row[c.key]);
      if (missingRequired) {
        out.push({ raw: line, row, ok: false, reason: `${missingRequired.label} is required` });
      } else {
        const conv = toInput(row);
        if (conv === null) out.push({ raw: line, row, ok: false, reason: 'Skipped (invalid)' });
        else out.push({ raw: line, row, ok: true });
      }
    }
    setParsed(out);
    setStep('preview');
  }

  async function doImport() {
    setStep('importing');
    setError(null);
    let done = 0;
    const valid = parsed.filter((p) => p.ok);
    setProgress({ done: 0, total: valid.length });
    for (const p of valid) {
      try {
        const input = toInput(p.row);
        if (input !== null) {
          await createRow(input);
          done += 1;
          setProgress({ done, total: valid.length });
        }
      } catch (e) {
        setError((e instanceof Error ? e.message : String(e)) + ' — stopped after ' + done + ' rows.');
        setStep('preview');
        return;
      }
    }
    setStep('done');
    onImported(done);
  }

  function reset() {
    setCsv('');
    setParsed([]);
    setStep('paste');
    setError(null);
    setProgress({ done: 0, total: 0 });
  }

  return (
    <Modal open={open} title={title} onClose={onClose}>
      {error && <div className="banner banner-error">{error}</div>}

      {step === 'paste' && (
        <>
          <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
            Paste from a spreadsheet. One row per line. Columns:
          </p>
          <div className="card" style={{ marginBottom: 12, background: 'var(--brand-pale)' }}>
            <p style={{ margin: '0 0 6px', fontWeight: 600 }}>
              Order: {columns.map((c) => c.label + (c.required ? ' *' : '')).join(' · ')}
            </p>
            {columns.some((c) => c.hint) && (
              <ul style={{ margin: '0 0 6px 0', paddingLeft: 18, fontSize: 12 }}>
                {columns.filter((c) => c.hint).map((c) => (
                  <li key={c.key}><strong>{c.label}:</strong> {c.hint}</li>
                ))}
              </ul>
            )}
            <p style={{ margin: '8px 0 0', fontSize: 12 }}><em>Example:</em></p>
            <pre style={{ margin: '4px 0 0', fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {exampleRows.join('\n')}
            </pre>
          </div>
          <div className="field">
            <label htmlFor="csv">Paste rows here</label>
            <textarea
              id="csv"
              rows={8}
              value={csv}
              onChange={(e) => setCsv(e.target.value)}
              placeholder="name,serial,loler,pat&#10;Telehandler,JCB540170-2024-0019,2025-09-01,2025-12-01&#10;..."
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
          </div>
          <div className="btn-stack">
            <button type="button" className="btn btn-primary" onClick={parseCsv} disabled={!csv.trim()}>
              Parse rows →
            </button>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          </div>
        </>
      )}

      {step === 'preview' && (
        <>
          <p style={{ marginTop: 0 }}>
            <strong>{parsed.filter((p) => p.ok).length}</strong> rows ready.{' '}
            {parsed.filter((p) => !p.ok).length > 0 && (
              <span className="muted">{parsed.filter((p) => !p.ok).length} will be skipped.</span>
            )}
          </p>
          <div className="card" style={{ marginBottom: 12, maxHeight: 240, overflowY: 'auto', padding: 8 }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--brand-pale)' }}>
                  <th style={{ textAlign: 'left', padding: '4px 6px' }}>#</th>
                  {columns.map((c) => (
                    <th key={c.key} style={{ textAlign: 'left', padding: '4px 6px' }}>{c.label}</th>
                  ))}
                  <th style={{ textAlign: 'left', padding: '4px 6px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {parsed.map((p, i) => (
                  <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '4px 6px' }}>{i + 1}</td>
                    {columns.map((c) => (
                      <td key={c.key} style={{ padding: '4px 6px' }}>{p.row[c.key] || '—'}</td>
                    ))}
                    <td style={{ padding: '4px 6px' }}>
                      {p.ok ? <span className="status-pill status-ok">OK</span> : <span className="status-pill status-danger" title={p.reason}>{p.reason}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="btn-stack">
            <button
              type="button"
              className="btn btn-primary"
              onClick={doImport}
              disabled={parsed.filter((p) => p.ok).length === 0}
            >
              Import {parsed.filter((p) => p.ok).length} rows
            </button>
            <button type="button" className="btn btn-ghost" onClick={reset}>← Back to paste</button>
          </div>
        </>
      )}

      {step === 'importing' && (
        <>
          <p>Importing… {progress.done} of {progress.total}</p>
          <div className="cpp-progress-bar"><div className="cpp-progress-fill" style={{ width: `${(progress.done / Math.max(1, progress.total)) * 100}%` }} /></div>
        </>
      )}

      {step === 'done' && (
        <>
          <div className="card" style={{ background: 'var(--brand-pale)', marginBottom: 12 }}>
            <p style={{ margin: 0 }}><strong>✓ Imported {progress.done} rows.</strong></p>
          </div>
          <div className="btn-stack">
            <button type="button" className="btn btn-primary" onClick={onClose}>Done</button>
            <button type="button" className="btn btn-ghost" onClick={reset}>Import more</button>
          </div>
        </>
      )}
    </Modal>
  );
}

/** Minimal CSV line splitter — handles double-quoted cells with commas. */
function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let i = 0;
  let cur = '';
  let inQuotes = false;
  while (i < line.length) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i += 2; continue; }
      if (ch === '"') { inQuotes = false; i += 1; continue; }
      cur += ch; i += 1; continue;
    } else {
      if (ch === '"') { inQuotes = true; i += 1; continue; }
      if (ch === ',') { out.push(cur); cur = ''; i += 1; continue; }
      cur += ch; i += 1;
    }
  }
  out.push(cur);
  return out;
}
